import React from 'react';

import type * as PanelTypes from '../../state/views/panel/types';
import {type PartRefFromObjSchema} from '../../state/views/types';
import {mediaTypeToComponent} from '../../util/mediaTypeToComponent';
import {
  useRampFlagMediaPanelReactFetch,
  useRampFlagVideoStreaming,
} from '../../util/rampFeatureFlags/rampFeatureFlags';
import {type MediaSize} from '../ImageCard/resolveMediaSize';
import {
  type TileMedia,
  type TileMediaSpec,
  type TileMediaTracking,
} from '../MediaCard';
import MessageMediaNotFound from '../MessageMediaNotFound';
import {useRecordPanelErrored} from '../Panel/panelAvailability';
import {InstrumentedCenteredWaveLoader as Loader} from './../../components/utility/InstrumentedLoader';
import {type MediaBrowserPanelConfig} from '.';
import {isComplete, useTileMedia} from './fetch';
import {getKeyForTileMediaSpec} from './fetch/keys';
import {useMediaBrowserLoading} from './MediaBrowserLoadingContext';
import {useMediaBrowserPanelTileTrackingContextMaybe} from './MediaBrowserPanelTileTrackingContext';
import {useTileMediaStatusPoll} from './useTileMediaStatusPoll';

type TileMediaLoaderProps = {
  tileMediaSpec: TileMediaSpec;
  attemptNonexistentSteps: boolean;
  width: number;
  height: number;
  mediaWidth?: number;
  mediaHeight?: number;
  actualSize: boolean;
  controls: any;
  mediaPanelRef: PartRefFromObjSchema<PanelTypes.PanelObjSchema>;
  moleculeConfig?: any;
  disableRunLink?: boolean;
  maskOptions?: any;
  config: any;
  run: any;
  runSignature: any;
  runNotes?: string;
  customRunName?: string;
  labels: any[];
  globalStep: number;
  mediaKey: string;
  mediaIndex: number;
  omitRunLabel?: boolean;
  omitCaptions?: boolean;
  getMediaTracking?: (tileMediaSpec: TileMediaSpec) => TileMediaTracking | null;
  isWithinMediaBrowserModal?: boolean;
  onImageRender?: (size: MediaSize) => void;
};

function useShouldLoadTileMediaInBackground(
  tileMediaSpec: TileMediaSpec,
  tileMedia: TileMedia | null,
  config: MediaBrowserPanelConfig
) {
  const isVideoStreamingEnabled = useRampFlagVideoStreaming(
    tileMediaSpec.run.entityName
  );
  const isSyncEnabled = config.videoPlaybackSync;

  return (
    isVideoStreamingEnabled &&
    !isSyncEnabled &&
    tileMediaSpec.mediaType === 'video' &&
    tileMedia?.hlsSrc != null &&
    config.disableVideoStream !== true
  );
}

export const TileMediaLoader: React.FC<TileMediaLoaderProps> = React.memo(
  ({
    tileMediaSpec,
    attemptNonexistentSteps,
    width,
    height,
    omitRunLabel,
    omitCaptions,
    ...props
  }) => {
    const isUpstreamLoading = useMediaBrowserLoading();
    const {
      loading,
      tileMedia,
      status,
      getTileMediaTracking,
      invalidateAndRefetch,
    } = useTileMedia(
      tileMediaSpec,
      attemptNonexistentSteps,
      props.config,
      isUpstreamLoading
    );
    const {setStatusObservedPerformanceMs} =
      useMediaBrowserPanelTileTrackingContextMaybe();

    const shouldUseReactFetch = useRampFlagMediaPanelReactFetch() ?? false;
    const tileMediaSpecKey = getKeyForTileMediaSpec(tileMediaSpec);
    useTileMediaStatusPoll(tileMediaSpecKey, status, !shouldUseReactFetch);

    // Stamp when React first sees isComplete so pollWait is the 100ms
    // IN_MEMORY_CACHE poll (or ~0 on the Apollo path), not element load.
    if (isComplete(status)) {
      // oxlint-disable-next-line react/purity
      setStatusObservedPerformanceMs(performance.now());
    }

    useRecordPanelErrored(
      'Media Browser',
      status?.hasError ? (status.errorKind ?? 'media_fetch_failed') : null
    );

    const MediaComponent = mediaTypeToComponent(tileMediaSpec.mediaType);

    const shouldLoadTileMediaInBackground = useShouldLoadTileMediaInBackground(
      tileMediaSpec,
      tileMedia,
      props.config
    );

    if ((loading || !status) && !shouldLoadTileMediaInBackground) {
      return (
        <div style={{width, height}}>
          <Loader name="panel-content-loader" size="small" />
        </div>
      );
    }

    if (status?.hasError) {
      // Only show error message for media tiles, not overlay tiles
      // This prevents duplicate error messages when both media and overlay tiles fail
      const isOverlayTile =
        props.maskOptions &&
        !props.maskOptions.showImage &&
        props.maskOptions.maskKeys.length > 0;

      if (isOverlayTile) {
        // For overlay tiles, render an empty div with the same dimensions
        return <div className="mt-6" style={{width, maxHeight: height}} />;
      }

      return (
        <div className="w-full h-full min-w-0 min-h-0 flex items-center justify-center overflow-hidden">
          <div
            className="ui card image-card content-card mt-6 max-w-full"
            data-test="image-content-card"
            style={{
              width,
              maxHeight: height,
            }}>
            <div
              className="image-card-image w-full"
              style={{maxHeight: height}}>
              <div className="flex items-center justify-center">
                <MessageMediaNotFound
                  runName={tileMediaSpec.run.name}
                  stepIndex={tileMediaSpec.mediaStep}
                  mediaKey={tileMediaSpec.mediaKey}
                  mediaIndex={tileMediaSpec.mediaIndex}
                  maxWidth={width}
                  height={height}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      // oxlint-disable-next-line react/static-components
      <MediaComponent
        {...props}
        width={width}
        height={height}
        tileMedia={tileMedia}
        tileMediaSpec={tileMediaSpec}
        getMediaTracking={getTileMediaTracking}
        invalidateAndRefetch={invalidateAndRefetch}
        omitRunLabel={omitRunLabel}
        omitCaptions={omitCaptions}
      />
    );
  }
);
